// src/components/ProductImageForm.jsx
import React, { useEffect, useState } from "react";

export default function ProductImageForm({ initial, onSave, onCancel }) {
  const EMPTY = { id: "", DistributorCode: "", ImageUrl: "" };
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setForm({ ...EMPTY, ...(initial || {}) });
    setFile(null);
    setPreview(null);
  }, [initial]);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const serverBase = "https://modularobjects.com/products";
  const existingImageUrl = form.id
    ? `${serverBase}/api/product/images/${encodeURIComponent(form.id)}/image`
    : null;
  const imgSrc = preview || (form.id ? `${existingImageUrl}?ts=${Date.now()}` : null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      id: form.id || undefined,
      DistributorCode: form.DistributorCode || null,
      ImageUrl: form.ImageUrl || null,
    };
    onSave?.(payload, file);
  };

  return (
    <div className="bg-white dark:bg-neutral-800 p-4 rounded shadow">
      <h3 className="text-xl font-semibold mb-4">
        {form.id ? "Edit image" : "Create image"}
      </h3>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* left: preview + file upload */}
          <div>
            <div className="mb-2 font-medium">Image</div>
            <div className="w-28 h-28 border rounded overflow-hidden flex items-center justify-center bg-neutral-50">
              {imgSrc ? (
                <img
                  key={imgSrc}
                  src={imgSrc}
                  alt="preview"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.src = ""; }}
                />
              ) : (
                <span className="text-sm text-gray-500">No image</span>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              className="mt-2 block w-full text-sm"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>

          {/* right: fields */}
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="ID" disabled value={form.id} onChange={(v) => update("id", v)} />
            <Field label="Distributor Code" required value={form.DistributorCode} onChange={(v) => update("DistributorCode", v)} />
            <Field label="Image URL" value={form.ImageUrl} onChange={(v) => update("ImageUrl", v)} />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded border">Cancel</button>
          <button type="submit" className="bg-primary-light text-white px-4 py-2 rounded hover:bg-primary-dark">Save</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, disabled }) {
  return (
    <div>
      <label className="block text-sm mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        disabled={disabled}
        required={required}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full px-3 py-2 border rounded dark:bg-neutral-700 dark:text-white disabled:opacity-60"
      />
    </div>
  );
}
